import { useState } from 'react'
import type { ConfigFilters, SelectOption, TagRow } from '../../types'
import type {
  ColumnField,
  ColumnFilterDef,
  ColumnValues,
  ConfigFilterBarProps,
  SelectColumnField,
} from './ConfigFilterBar.types'
import { collectPairs } from './helpers/collectPairs'
import { buildRowsFromPairs } from './helpers/buildRowsFromPairs'
import { SearchableSelect } from '../SearchableSelect/SearchableSelect'
import './ConfigFilterBar.css'

const EMPTY_FILTERS: ConfigFilters = {
  tenant: '',
  isp: '',
  venue: '',
  configKey: '',
  tags: [],
}

// The four exact-match column filters, rendered above the dynamic tag rows.
// Driving them off this list keeps the markup DRY and the labels in one place.
const COLUMN_FILTERS: ColumnFilterDef[] = [
  { field: 'tenant', label: 'Tenant', kind: 'select' },
  { field: 'isp', label: 'ISP', kind: 'select' },
  { field: 'venue', label: 'Venue', kind: 'select' },
  {
    field: 'configKey',
    label: 'Config Key',
    kind: 'text',
    placeholder: 'Config key',
  },
]

function hasAnyColumnValue(columns: ColumnValues): boolean {
  return COLUMN_FILTERS.some(({ field }) => columns[field].trim().length > 0)
}

// Unified config filter bar: four exact-match column inputs
// (tenant/ISP/venue/configKey) plus the dynamic tag rows. Edits stay local —
// only clicking "Filter" emits the applied filters through onChange, so
// ConfigsPage refetches once per intentional apply rather than on every
// keystroke. All applied filters AND-combine server-side. When seeded with
// initialFilters (from the URL) it starts expanded with the fields pre-filled.
export function ConfigFilterBar({
  onChange,
  initialFilters = EMPTY_FILTERS,
  tenantOptions = [],
  ispOptions = [],
  venueOptions = [],
}: ConfigFilterBarProps) {
  const selectOptionsByField: Record<SelectColumnField, SelectOption[]> = {
    tenant: tenantOptions,
    isp: ispOptions,
    venue: venueOptions,
  }

  const [columns, setColumns] = useState<ColumnValues>({
    tenant: initialFilters.tenant,
    isp: initialFilters.isp,
    venue: initialFilters.venue,
    configKey: initialFilters.configKey,
  })
  const [rows, setRows] = useState<TagRow[]>(() =>
    buildRowsFromPairs(initialFilters.tags)
  )
  const [expanded, setExpanded] = useState(
    hasAnyColumnValue(columns) || rows.length > 0
  )

  function updateColumn(field: ColumnField, value: string) {
    setColumns({ ...columns, [field]: value })
  }

  function updateRow(index: number, patch: Partial<TagRow>) {
    setRows(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)))
  }

  function addRow() {
    setRows([...rows, { key: '', value: '' }])
  }

  function removeRow(index: number) {
    setRows(rows.filter((_, i) => i !== index))
  }

  function applyFilter() {
    onChange({ ...columns, tags: collectPairs(rows) })
  }

  // Clear every field and reset the applied filter so the list returns unfiltered.
  function removeAllFilters() {
    setColumns({ tenant: '', isp: '', venue: '', configKey: '' })
    setRows([])
    onChange(EMPTY_FILTERS)
  }

  const hasActiveFilter = hasAnyColumnValue(columns) || rows.length > 0

  // The toggle is a normal top-left element in both states, so switching between
  // "Filters +" and "Filters -" never shifts its position. Collapsed renders only
  // the toggle inside the bordered box; expanded reveals the body below it.
  return (
    <div className="config-filter-bar">
      <button
        type="button"
        className="config-filter-bar-toggle"
        aria-expanded={expanded}
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? '-' : '+'} Filters
      </button>

      {expanded && (
        <div className="config-filter-bar-body">
          {COLUMN_FILTERS.map((column) => (
            <div key={column.field} className="config-filter-bar-column">
              <label
                className="config-filter-bar-column-label"
                htmlFor={`config-filter-${column.field}`}
              >
                {column.label}
              </label>
              {column.kind === 'select' ? (
                <SearchableSelect
                  id={`config-filter-${column.field}`}
                  className="config-filter-bar-column-input"
                  value={columns[column.field]}
                  options={selectOptionsByField[column.field]}
                  placeholder="All"
                  ariaLabel={column.label}
                  onChange={(value) => updateColumn(column.field, value)}
                />
              ) : (
                <input
                  id={`config-filter-${column.field}`}
                  type="text"
                  className="form-control form-control-sm config-filter-bar-column-input"
                  placeholder={column.placeholder}
                  value={columns[column.field]}
                  onChange={(e) => updateColumn(column.field, e.target.value)}
                />
              )}
            </div>
          ))}

          {rows.map((row, index) => (
            <div key={index} className="config-filter-bar-row">
              <span className="config-filter-bar-row-label">Tag Filter</span>
              <input
                type="text"
                className="form-control form-control-sm config-filter-bar-key"
                placeholder="Key"
                aria-label={`Filter tag key ${index + 1}`}
                value={row.key}
                onChange={(e) => updateRow(index, { key: e.target.value })}
              />
              <input
                type="text"
                className="form-control form-control-sm config-filter-bar-value"
                placeholder="Value"
                aria-label={`Filter tag value ${index + 1}`}
                value={row.value}
                onChange={(e) => updateRow(index, { value: e.target.value })}
              />
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                aria-label={`Remove tag filter ${index + 1}`}
                onClick={() => removeRow(index)}
              >
                Remove
              </button>
            </div>
          ))}

          <div className="config-filter-bar-footer">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={addRow}
            >
              Add tag filter
            </button>
            {hasActiveFilter && (
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={removeAllFilters}
              >
                Remove filters
              </button>
            )}
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={applyFilter}
            >
              Filter
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
